// ---- Weather dev panel (F4) --------------------------------------------------
// A toggleable control surface for testing every weather state at runtime:
// sliders for each axis, a ramp slider (snap ↔ natural-speed transitions),
// kind + preset buttons, day/moon/season jumps, and a drift freeze. Sends
// `weatherDev` intents; the server only honors them when it booted with
// WEATHER_DEV=1 (the `welcome.dev` flag tells us — otherwise the panel renders
// read-only with a notice). The echoed `weather` broadcast is the ack, and it
// reaches EVERY client: weather is one global state, so an override is
// world-wide (by design — useful for multiplayer testing).
//
// Follows the imdebug.ts (F3) pattern, except this panel takes pointer input.

import { sendWeatherDev, serverWeatherDev } from "./net.ts";
import { weatherDebugState } from "./weather.ts";
import { moonPhase, YEAR_DAYS, COLD_FROM, type PrecipKind } from "./shared.ts";

let panel: HTMLDivElement;
let readout: HTMLDivElement;
let notice: HTMLDivElement;
let controls: HTMLDivElement;
let on = false;
let lastRender = 0;
let ramp = 2; // seconds every send eases over (0 = snap, 60 = slow front)
const axisSliders = new Map<string, HTMLInputElement>();

const PHASE_NAMES = ["new", "wax cres", "1st qtr", "wax gibb", "FULL", "wan gibb", "3rd qtr", "wan cres"];
const cloudLabel = (c: number): string => (c < 0.35 ? "sunny" : c < 0.75 ? "partly cloudy" : "overcast");

type Preset = { name: string; set: { cloud: number; precip: number; kind: PrecipKind; storm: number; wind: number } };
const PRESETS: Preset[] = [
  { name: "Clear",    set: { cloud: 0.1, precip: 0,    kind: "none", storm: 0, wind: 0.2 } },
  { name: "Overcast", set: { cloud: 1,   precip: 0,    kind: "none", storm: 0, wind: 0.35 } },
  { name: "Drizzle",  set: { cloud: 0.7, precip: 0.25, kind: "rain", storm: 0, wind: 0.3 } },
  { name: "Downpour", set: { cloud: 1,   precip: 1,    kind: "rain", storm: 0, wind: 0.55 } },
  { name: "Blizzard", set: { cloud: 1,   precip: 1,    kind: "snow", storm: 0, wind: 0.8 } },
  { name: "Storm",    set: { cloud: 1,   precip: 0.9,  kind: "rain", storm: 1, wind: 0.75 } },
];

const BTN =
  "font:11px ui-monospace, Menlo, monospace; color:#cfe3cf; background:#1c241c;" +
  "border:1px solid #3a4a3a; border-radius:5px; padding:2px 7px; cursor:pointer;";

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.style.cssText = BTN;
  b.onclick = onClick;
  return b;
}

function row(parent: HTMLElement, gap = 4): HTMLDivElement {
  const d = document.createElement("div");
  d.style.cssText = `display:flex; flex-wrap:wrap; gap:${gap}px; margin-top:6px; align-items:center;`;
  parent.appendChild(d);
  return d;
}

function slider(
  parent: HTMLElement, label: string, min: number, max: number, step: number,
  value: number, onInput: (v: number) => void,
): HTMLInputElement {
  const wrap = row(parent);
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.style.cssText = "width:52px; display:inline-block;";
  const s = document.createElement("input");
  s.type = "range";
  s.min = String(min); s.max = String(max); s.step = String(step); s.value = String(value);
  s.style.cssText = "flex:1; min-width:120px; accent-color:#6fae6f;";
  s.oninput = () => onInput(Number(s.value));
  wrap.append(lab, s);
  return s;
}

export function initWeatherDev(): void {
  panel = document.createElement("div");
  panel.id = "weatherdev";
  panel.style.cssText =
    "position:fixed; top:14px; right:12px; z-index:7; display:none; width:265px;" +
    "font:11px/1.5 ui-monospace, Menlo, monospace; color:#cfe3cf;" +
    "background:rgba(12,16,12,.88); border:1px solid #3a4a3a; border-radius:8px;" +
    "padding:8px 10px; user-select:none;";
  // The panel takes clicks (unlike imdebug) but must never steal the canvas's
  // pointer handlers — stop events at the panel edge.
  panel.addEventListener("pointerdown", (e) => e.stopPropagation());

  const title = document.createElement("div");
  title.textContent = "⛅ weather dev (F4)";
  title.style.cssText = "font-weight:600; margin-bottom:2px;";
  panel.appendChild(title);

  notice = document.createElement("div");
  notice.style.cssText = "color:#c9a05a; white-space:pre-wrap;";
  panel.appendChild(notice);

  readout = document.createElement("div");
  readout.style.cssText = "white-space:pre; margin-top:4px;";
  panel.appendChild(readout);

  controls = document.createElement("div");
  panel.appendChild(controls);

  // Axis sliders — each input sends just that axis, eased over the ramp.
  for (const axis of ["cloud", "precip", "storm", "wind"] as const) {
    axisSliders.set(axis, slider(controls, axis, 0, 1, 0.05, 0, (v) => {
      sendWeatherDev({ set: { [axis]: v }, ramp });
    }));
  }
  // Ramp — applied to every send: 0 snaps, 45+ mimics a natural front.
  const rampLab = document.createElement("span");
  slider(controls, "ramp", 0, 60, 1, ramp, (v) => {
    ramp = v;
    rampLab.textContent = `${ramp}s`;
  }).parentElement!.appendChild(rampLab);
  rampLab.textContent = `${ramp}s`;
  rampLab.style.cssText = "width:26px; text-align:right;";

  // Precip kind.
  const kinds = row(controls);
  for (const kind of ["rain", "snow", "none"] as const) {
    kinds.appendChild(button(kind, () => sendWeatherDev({ set: { kind }, ramp })));
  }

  // Presets — full coherent blobs.
  const presets = row(controls);
  for (const p of PRESETS) {
    presets.appendChild(button(p.name, () => sendWeatherDev({ set: p.set, ramp })));
  }

  // Calendar: day steps, a jump to each moon phase, season jumps. Day jumps
  // snap (ramp 0) — the calendar isn't an eased quantity.
  const days = row(controls);
  days.appendChild(button("day −1", () => {
    sendWeatherDev({ set: { day: Math.max(0, weatherDebugState().day - 1) }, ramp: 0 });
  }));
  days.appendChild(button("day +1", () => {
    sendWeatherDev({ set: { day: weatherDebugState().day + 1 }, ramp: 0 });
  }));
  const phases = row(controls, 3);
  for (let p = 0; p < 8; p++) {
    const b = button(["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"][p], () => {
      const d = weatherDebugState().day;
      sendWeatherDev({ set: { day: d + ((p - moonPhase(d)) + 8) % 8 }, ramp: 0 });
    });
    b.title = PHASE_NAMES[p];
    b.style.padding = "2px 4px";
    phases.appendChild(b);
  }
  const seasons = row(controls);
  seasons.appendChild(button("☀ warm season", () => {
    const d = weatherDebugState().day;
    const into = d % YEAR_DAYS; // already warm → no-op; cold → jump to new year
    sendWeatherDev({ set: { day: into < COLD_FROM ? d : d + (YEAR_DAYS - into) }, ramp: 0 });
  }));
  seasons.appendChild(button("❄ cold season", () => {
    const d = weatherDebugState().day;
    const into = d % YEAR_DAYS;
    sendWeatherDev({ set: { day: into >= COLD_FROM ? d : d + (COLD_FROM - into) }, ramp: 0 });
  }));

  // Drift freeze/resume.
  const freeze = row(controls);
  freeze.appendChild(button("❄ freeze drift", () => sendWeatherDev({ freeze: true })));
  freeze.appendChild(button("▶ resume drift", () => sendWeatherDev({ freeze: false })));

  document.body.appendChild(panel);
}

export function toggleWeatherDev(): void {
  on = !on;
  panel.style.display = on ? "block" : "none";
  if (on) renderPanel(performance.now());
}

/** Per-frame from game.ts; renders at ~4Hz while open. */
export function updateWeatherDev(): void {
  if (!on) return;
  const now = performance.now();
  if (now - lastRender < 250) return;
  renderPanel(now);
}

function renderPanel(now: number): void {
  lastRender = now;
  const s = weatherDebugState();
  const canDrive = serverWeatherDev;
  notice.textContent = canDrive ? "" : "WEATHER_DEV off on this server — read-only";
  controls.style.display = canDrive ? "block" : "none";
  const f = (x: number) => x.toFixed(2);
  readout.textContent =
    `cloud  ${f(s.cur.cloud)} → ${f(s.tgt.cloud)}  (${cloudLabel(s.cur.cloud)})\n` +
    `precip ${f(s.cur.precip)} → ${f(s.tgt.precip)}  ${s.cur.kind}\n` +
    `storm  ${f(s.cur.storm)} → ${f(s.tgt.storm)}\n` +
    `wind   ${f(s.cur.wind)} → ${f(s.tgt.wind)}\n` +
    `ramp   ${s.rampLeft.toFixed(1)}s left${s.frozen ? "   ❄ FROZEN" : ""}\n` +
    `day ${s.day}  ${PHASE_NAMES[s.phase]} moon  ${s.cold ? "cold" : "warm"} season`;
  // Keep the sliders tracking reality while the user isn't mid-drag.
  for (const [axis, el] of axisSliders) {
    if (document.activeElement !== el) {
      el.value = String((s.tgt as unknown as Record<string, number>)[axis]);
    }
  }
}
